<svelte:options customElement="rf-overlay" />

<script module>
	/**
	 * Types
	 */

	export interface ReframeOverlayProps {
		id?: string;
		tag: string;
		debug?: boolean;
		'canvas-fit'?: 'auto' | 'contain' | 'cover';
		'no-collection'?: boolean;
	}
</script>

<script lang="ts">
	/**
	 * Import
	 */
	import type { CanvasPanelMoveEvent } from '@src/types/canvas-panel.d.ts';
	import RfNotificationCentre from '@core/elements/rf-notification-centre/rf-notification-centre.svelte';
	import { v4 as uuidv4 } from 'uuid';
	import { useSlottedElements } from '@src/composables/useSlottedElements';
	import { useCollection } from '@src/composables/useCollection.svelte.ts';
	import { getComponentNotifications, addNotification } from '@store/notification-centre.svelte';
	import { createEventDispatcher, onMount, onDestroy } from 'svelte';
	import { get } from 'svelte/store';
	import type { RFCanvas } from '@src/types/reframe';
	import math from '@src/utils/math';
	import parseBool from '@src/utils/bool';
	/**
	 * Props
	 */
	const {
		id = undefined,
		tag = 'section',
		'canvas-fit': canvasFit = 'auto',
		debug = false,
		'no-collection': noCollection = false,
	}: ReframeOverlayProps = $props();

	/**
	 * Constants
	 */
	export const name = 'rf-overlay';
	const componentId = uuidv4();

	/**
	 * Variables
	 */

	let progress = $state(0.5);

	let mouse = {
		past: { x: 0.5, y: 0 },
		delta: { x: 0, y: 0 },
	};
	let currentCanvasTarget: { x: number; y: number; width: number; height: number } | null = null;
	let canvases: { current: RFCanvas | null; left: RFCanvas | null; right: RFCanvas | null } = {
		current: null,
		left: null,
		right: null,
	};

	let isGrabbing = false;
	let rafId: number | null = null;

	/**
	 * Derived
	 */
	const collectionNotifs = $derived(getComponentNotifications(componentId));

	/**
	 * Composables
	 */

	const { element, getSlottedElements } = useSlottedElements();

	export const collection = parseBool(noCollection)
		? undefined
		: useCollection(id, {
				element,
				moduleName: name,
				componentId,
			});

	/**
	 * Methods
	 */

	const dispatch = createEventDispatcher();

	/**
	 * On each frame, sync the interacted canvas position with the puppet one
	 */
	const update = () => {
		if (canvases.current && currentCanvasTarget && canvases.left && canvases.right) {
			const isLeftCanvas = canvases.current === canvases.left;
			const { x, y, width, height } = currentCanvasTarget;

			const normalizedX = x / canvases.current.naturalWidth;
			const normalizedY = y / canvases.current.naturalHeight;
			const normalizedW = width / canvases.current.naturalWidth;
			const normalizedH = height / canvases.current.naturalHeight;

			const puppetW = isLeftCanvas ? canvases.right.naturalWidth : canvases.left.naturalWidth;
			const puppetH = isLeftCanvas ? canvases.right.naturalHeight : canvases.left.naturalHeight;
			const targetX = normalizedX * puppetW;
			const targetY = normalizedY * puppetH;

			const targetW = normalizedW * puppetW;
			const targetH = normalizedH * puppetH;

			if (!isLeftCanvas) canvases.left.goToTarget({ x: targetX, y: targetY, width: targetW, height: targetH }, { immediate: true });
			else canvases.right.goToTarget({ x: targetX, y: targetY, width: targetW, height: targetH }, { immediate: true });
		}

		rafId = requestAnimationFrame(update);
	};

	/**
	 * Events
	 */

	/**
	 * Update the separator position (normalized [0 -> 1]) with the mouse delta
	 */
	function updateProgress(x: number, y: number) {
		const container = get(element);
		if (!isGrabbing || !container) return;

		const bounds = container.getBoundingClientRect();
		const normalizedX = (x - bounds.x) / bounds.width;

		mouse.delta.x = mouse.past.x - normalizedX;
		progress -= mouse.delta.x;
		mouse.past = { x: normalizedX, y };
	}

	/**
	 * When users click down on the control thumb or touchstart
	 */
	function startGrab(x: number, y: number) {
		mouse.past = { x, y };
		isGrabbing = true;
		dispatch('grab', isGrabbing);
	}

	/**
	 * When users move the mouse or touchmove
	 */
	const handleMouseMove = (e: MouseEvent) => {
		updateProgress(e.clientX, e.clientY);
	};

	const handleTouchMove = (e: TouchEvent) => {
		const touch = e.touches[0];
		if (!touch) return;
		updateProgress(touch.clientX, touch.clientY);
		e.preventDefault();
	};

	/**
	 * When users click down on the control thumb or touchstart
	 */
	const onTouchStart = (e: TouchEvent) => {
		const touch = e.touches[0];
		if (!touch) return;

		const container = get(element);
		if (!container) return;

		const bounds = container.getBoundingClientRect();
		const x = (touch.clientX - bounds.x) / bounds.width;

		startGrab(x, touch.clientY);
		e.preventDefault();
	};

	const onDown = () => {
		startGrab(mouse.past.x, mouse.past.y);
	};

	/**
	 * When users click up on the window or touchend
	 */
	const onUp = () => {
		isGrabbing = false;
		dispatch('grab', isGrabbing);
	};

	const onMouseOver = (e: MouseEvent) => {
		if (rafId) {
			cancelAnimationFrame(rafId);
			rafId = null;
		}
		canvases.left?.canvasPanel?.removeEventListener('move', onCanvasPanelMove);
		canvases.right?.canvasPanel?.removeEventListener('move', onCanvasPanelMove);

		canvases.current = e.target as RFCanvas;

		if (canvases.current === canvases.left) canvases.left?.canvasPanel?.addEventListener('move', onCanvasPanelMove);
		else canvases.right?.canvasPanel?.addEventListener('move', onCanvasPanelMove);
	};

	const onCanvasPanelMove = (e: CanvasPanelMoveEvent) => {
		currentCanvasTarget = e.detail;

		if (!rafId) update();
	};

	/**
	 * Set rf-canvas attributes to match rf-overlay logics
	 */
	const setCanvas = (_canvas: RFCanvas) => {
		_canvas.addEventListener('mousemove', onMouseOver);
		_canvas.setAttribute('legend', 'false');
		_canvas.setAttribute('interactive-mode', 'keydown');
		_canvas.setAttribute('interactive', 'true');
		_canvas.setAttribute('move-events', 'true');
		_canvas.setAttribute('granular-move-events', 'true');
		_canvas.setAttribute('data-active', 'true');
		_canvas.setAttribute('fit', canvasFit);
		return _canvas;
	};

	onMount(() => {
		const items = getSlottedElements();
		const slottedCanvases: RFCanvas[] = items.filter((el) => {
			return el.tagName === 'RF-CANVAS';
		}) as RFCanvas[];

		if (slottedCanvases.length !== 2) {
			addNotification({
				componentId,
				title: 'Amount of canvases',
				message: 'The amount of slotted canvases is not equal to two.',
				severity: 'warning',
				dismissable: true,
			});
		}

		if (slottedCanvases[0]) canvases.left = setCanvas(slottedCanvases[0]);

		if (slottedCanvases[1]) canvases.right = setCanvas(slottedCanvases[1]);
	});

	onDestroy(() => {
		if (rafId) cancelAnimationFrame(rafId);
	});
</script>

<!-- Listen for mousemove on the window -->
<svelte:window onmousemove={handleMouseMove} onmouseup={onUp} ontouchmove={handleTouchMove} ontouchend={onDown} />

<slot name="header"></slot>
<svelte:element
	this={tag}
	class="rf-overlay"
	part="rf-overlay"
	bind:this={$element}
	style="--rf-overlay-separator: {math.clamp(progress, 0, 1).toFixed(4)};"
>
	{#if collectionNotifs && debug}
		<RfNotificationCentre notifications={collectionNotifs} />
	{/if}
	<slot></slot>
	<div class="rf-overlay__control">
		<button aria-label="button" class="rf-overlay__control__thumb" onmousedown={onDown} ontouchstart={onTouchStart}>
			<div class="rf-overlay__control__thumb__tip"></div>
		</button>
	</div>
</svelte:element>
<slot name="footer"></slot>

<style lang="scss" global>
	.rf-overlay {
		display: flex;
		flex-direction: column;
		position: relative;
		overflow: hidden;
		touch-action: pan-y;
		--rf-overlay-separator: 0.5;

		::slotted(rf-canvas) {
			width: 100%;
			position: relative;
			clip-path: inset(0 calc(100% - var(--rf-overlay-separator, 0.5) * 100%) 0 0);
		}

		::slotted(rf-canvas:nth-child(2)) {
			position: absolute;
			clip-path: inset(0 0 0 calc(var(--rf-overlay-separator, 0.5) * 100%));
		}

		::slotted(rf-canvas:not([data-active='true'])) {
			display: none; // Only show the first two
			clip-path: unset;
		}

		&__control {
			position: absolute;
			top: 0;
			left: 0;
			width: 100%;
			height: 100%;
			transform: translateX(calc(var(--rf-overlay-separator, 0.5) * 100%));
			pointer-events: none;
			background-color: transparent;
			z-index: 2;

			&__thumb {
				position: absolute;
				display: block;
				top: 0;
				height: 100%;
				width: 4px;
				background-color: hsla(0, 0%, 10%, 1);
				cursor: grab;
				pointer-events: all;
				outline: none;
				padding: 0;
				border: 0;

				&__tip {
					position: absolute;
					top: 50%;
					left: 50%;
					height: 100px;
					width: 10px;
					transform: translate(-50%, -50%);
					background-color: hsla(0, 0%, 10%, 1);
					outline: hsl(0, 0%, 80%) 1px solid;
					border-radius: 2px;

					&::after {
						content: '';
						position: absolute;

						top: 50%;
						left: 50%;
						height: 60%;
						background-color: hsl(0, 0%, 80%);
						width: 1px;
						transform: translate(-50%, -50%);
					}
				}
			}
		}
	}
</style>
